import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { PreferencesInnerModal } from '../PreferencesInnerModal';

import { StyledValuePropWrapper, StyledPreferenceResetButton, StyledPreferenceTextArea } from './styles';
import { useValuePropositionPreferences } from './useValuePropositionPreferences';

type ValuePropositionProps = {
  isOpen: boolean;
  onClose: () => void;
  onCloseInnerModal: () => void;
};

const TEXTAREA_HEIGHT = 200;

export const ValueProposition = ({ isOpen, onClose, onCloseInnerModal }: ValuePropositionProps) => {
  const { t } = useTranslation();
  const painPointRef = useRef<HTMLTextAreaElement>(null);

  const {
    painPoint,
    setPainPoint,
    valueProposition,
    setValueProposition,
    isSaveDisabled,
    isNoChangeInValues,
    isResetDisabled,
    isResetAvailable,
    isLoading,
    isMutationLoading,
    onReset,
    onSave,
  } = useValuePropositionPreferences(onClose);

  useEffect(() => {
    if (isOpen && painPointRef.current) {
      painPointRef.current.focus();
    }
  }, [isOpen]);

  return (
    <PreferencesInnerModal
      isOpen={isOpen}
      onClose={onClose}
      onReturn={onCloseInnerModal}
      title={t('insights.preferences.value_prop.title')}
      subtitle={t('insights.preferences.value_prop.subtitle')}
      isSaveDisabled={isSaveDisabled}
      tooltipContent={
        isNoChangeInValues
          ? t('insights.preferences.value_prop.no_change.tooltip')
          : t('insights.preferences.value_prop.disabled.tooltip')
      }
      onSave={onSave}
      isLoading={isMutationLoading}
    >
      <StyledValuePropWrapper>
        <StyledPreferenceTextArea
          ref={painPointRef}
          value={painPoint}
          onChange={setPainPoint}
          label={t('insights.preferences.value_prop.input_1.label') ?? ''}
          placeholder={t('insights.preferences.value_prop.input_1.placeholder')}
          isLoading={isLoading}
          height={TEXTAREA_HEIGHT}
        />
        <StyledPreferenceTextArea
          value={valueProposition}
          onChange={setValueProposition}
          label={t('insights.preferences.value_prop.input_2.label') ?? ''}
          placeholder={t('insights.preferences.value_prop.input_2.placeholder')}
          isLoading={isLoading}
          height={TEXTAREA_HEIGHT}
        />
        {isResetAvailable ? (
          <StyledPreferenceResetButton disabled={isResetDisabled} variant="flat" onClick={onReset}>
            <SWReactIcons
              iconName="refresh-target"
              size="xs"
              iconColor={isResetDisabled ? colorsPalettes.carbon[200] : colorsPalettes.blue[400]}
            />
            {t('insights.preferences.value_prop.reset')}
          </StyledPreferenceResetButton>
        ) : null}
      </StyledValuePropWrapper>
    </PreferencesInnerModal>
  );
};
